/**
 * User-friendly error messages for analysis operations.
 * Maps server error codes to clear messages with actionable guidance.
 */

export interface AnalysisErrorInfo {
  title: string;
  message: string;
  action?: string;
  canRetry: boolean;
  actionType?: 'retry' | 'reconnect' | 'contact' | 'upgrade' | 'refresh';
}

const PROVIDER_LABELS: Record<string, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
};

const providerLabel = (provider: string): string =>
  provider ? (PROVIDER_LABELS[provider.toLowerCase()] ?? provider) : '';

/**
 * Maps server error codes/messages to user-friendly error information.
 */
export function getAnalysisErrorInfo(error: {
  error?: string;
  message?: string;
  code?: string;
  details?: string;
  provider?: string;
  status?: number;
}): AnalysisErrorInfo {
  const errorCode = error.error || error.code || '';
  const errorMessage = error.message || error.details || '';
  const provider = error.provider || '';

  // OAuth/Authentication errors
  if (errorCode === 'token_expired') {
    return {
      title: 'Connection Expired',
      message: `Your ${provider || 'repository'} connection has expired.`,
      action: 'Reconnect your account in Settings to continue.',
      canRetry: false,
      actionType: 'reconnect',
    };
  }

  if (errorCode === 'token_validation_failed') {
    return {
      title: 'Connection Issue',
      message: `Unable to access your ${provider || 'repository'} account.`,
      action: 'Please reconnect your account in Settings.',
      canRetry: false,
      actionType: 'reconnect',
    };
  }

  // Clone auth failure: the account isn't connected (or was disconnected), so
  // a retry would just fail again — prompt a reconnect, not a session refresh.
  if (
    errorCode === 'connection_required' ||
    errorMessage.includes('connect your') ||
    errorMessage.includes('Authentication required')
  ) {
    return {
      title: 'Connect Your Account',
      message: `Connect your ${providerLabel(provider) || 'Git'} account to scan this repository.`,
      action: 'Connect your account in Settings, then try again.',
      canRetry: false,
      actionType: 'reconnect',
    };
  }

  if (errorCode === 'Authentication required' || error.status === 401) {
    return {
      title: 'Session Expired',
      message: 'Your session has expired.',
      action: 'Please sign in again to continue.',
      canRetry: false,
      actionType: 'refresh',
    };
  }

  // Repository errors
  if (errorCode === 'Repository not found' || errorMessage.includes('Repository not found')) {
    return {
      title: 'Repository Not Found',
      message: 'The repository could not be found or accessed.',
      action: 'Check that the repository exists and you have access to it.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  if (errorCode === 'Invalid repository URL') {
    return {
      title: 'Invalid Repository',
      message: 'The repository URL is not valid.',
      action: 'Please provide a valid GitHub, GitLab, or Bitbucket URL.',
      canRetry: false,
    };
  }

  if (errorMessage.includes('clone') || errorMessage.includes('Clone failed')) {
    return {
      title: 'Unable to Access Repository',
      message: 'Could not download the repository files.',
      action: 'Check that the repository is accessible and try again.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  // Subscription/billing errors
  if (errorCode === 'Subscription limit exceeded' || error.status === 403) {
    return {
      title: 'Plan Limit Reached',
      message: "You've reached your plan's analysis limit.",
      action: 'Upgrade your plan for more analyses.',
      canRetry: false,
      actionType: 'upgrade',
    };
  }

  // Rate limiting
  if (error.status === 429 || errorCode === 'Too many requests') {
    return {
      title: 'Too Many Requests',
      message: 'Please wait a moment before trying again.',
      action: 'Try again in a few minutes.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  // Analysis processing errors
  if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
    return {
      title: 'Analysis Timed Out',
      message: 'The analysis took longer than expected.',
      action: 'This can happen with large repositories. Try again.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  if (errorMessage.includes('container') || errorMessage.includes('Container')) {
    return {
      title: 'Processing Error',
      message: 'There was an issue processing your repository.',
      action: 'Our team has been notified. Please try again.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  if (errorCode === 'Invalid analysis data' || errorMessage.includes('validation')) {
    return {
      title: 'Analysis Error',
      message: 'There was an issue processing the analysis results.',
      action: 'Please try again. If this persists, contact support.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('Network') ||
    errorMessage.includes('fetch')
  ) {
    return {
      title: 'Connection Problem',
      message: 'Unable to connect to the server.',
      action: 'Check your internet connection and try again.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  // Server errors (5xx)
  if (error.status && error.status >= 500) {
    return {
      title: 'Server Error',
      message: 'Something went wrong on our end.',
      action: 'Please try again. If this persists, contact support.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  // Generic fallback with better messaging
  if (errorCode === 'Failed to start repository analysis') {
    return {
      title: 'Unable to Start Analysis',
      message: 'Could not start the analysis process.',
      action: 'Please try again.',
      canRetry: true,
      actionType: 'retry',
    };
  }

  // Default fallback
  return {
    title: 'Analysis Failed',
    message: errorMessage || 'An unexpected error occurred.',
    action: 'Please try again. If this continues, contact support.',
    canRetry: true,
    actionType: 'retry',
  };
}

/**
 * Extracts error information from various error response formats.
 */
export function parseAnalysisError(error: unknown): {
  error?: string;
  message?: string;
  code?: string;
  details?: string;
  provider?: string;
  status?: number;
} {
  if (!error) {
    return { message: 'An unknown error occurred' };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return { message: error };
  }

  // Handle Error objects
  if (error instanceof Error) {
    return { message: error.message };
  }

  // Handle structured error objects
  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;

    // Handle nested error.error object (common in WebSocket events)
    if (err.error && typeof err.error === 'object') {
      const nested = err.error as Record<string, unknown>;
      return {
        error: nested.error as string,
        message: (nested.userMessage || nested.message) as string,
        code: nested.code as string,
        details: nested.details as string,
        provider: nested.provider as string,
        status: nested.status as number,
      };
    }

    return {
      error: err.error as string,
      message: (err.userMessage || err.message) as string,
      code: err.code as string,
      details: err.details as string,
      provider: err.provider as string,
      status: err.status as number,
    };
  }

  return { message: 'An unknown error occurred' };
}
